import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';
import type { KeyAccount, KeyWallet } from '../api/types';
import { AddressWithCopy, PageHeader, TxHashLink } from '../components/common';

interface RotateResult {
  prevWallet: string;
  newWallet: string;
  keyType: string;
  keystoreDelivery: string | null;
  residual: { symbol: string; formatted: string }[];
  warning: string | null;
  txs: { addIdentity: string | null; addClaim: string | null };
}

/**
 * 키 생애주기 관리 (SFR-002, A-8).
 *
 * KMS 의 교체·폐기는 서비스 관점에서 부작용이 크다 — 교체는 **지갑 주소가 바뀌고**,
 * 폐기는 되돌릴 수 없다. 그래서 실행 전에 잔액·영향 범위를 보여주고 확인을 받는다.
 */
export function AdminKeys() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [rotate, setRotate] = useState<{ acct: KeyAccount; w: KeyWallet } | null>(null);
  const [revoke, setRevoke] = useState<{ acct: KeyAccount; w: KeyWallet } | null>(null);
  const [logsFor, setLogsFor] = useState<{ acct: KeyAccount; w: KeyWallet } | null>(null);
  const [password, setPassword] = useState('');
  const [force, setForce] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rotated, setRotated] = useState<RotateResult | null>(null);

  const list = useQuery({
    queryKey: ['admin-keys', q],
    queryFn: () => api.get<KeyAccount[]>(`/admin/keys${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });

  const doRotate = useMutation({
    mutationFn: () => api.post<RotateResult>(`/admin/keys/${rotate!.w.id}/rotate`, { password: password || undefined }),
    onSuccess: (r) => {
      setRotated(r); setRotate(null); setPassword(''); setErr(null);
      void qc.invalidateQueries({ queryKey: ['admin-keys'] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : '키 교체 실패'),
  });

  const doRevoke = useMutation({
    mutationFn: () => api.post(`/admin/keys/${revoke!.w.id}/revoke`, { force }),
    onSuccess: () => {
      setRevoke(null); setForce(false); setErr(null);
      void qc.invalidateQueries({ queryKey: ['admin-keys'] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : '키 폐기 실패'),
  });

  const accounts = list.data ?? [];

  return (
    <Box>
      <PageHeader
        crumb="시스템 관리자 · 키"
        title="키 관리"
        desc="사용자 지갑의 KMS 키 상태를 확인하고 교체·폐기합니다. 두 조치 모두 지갑 사용에 즉시 영향을 줍니다."
      />

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          size="small"
          label="아이디 검색"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="loginId 일부"
        />
      </Paper>

      {list.isLoading && <LinearProgress />}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>계정</TableCell><TableCell>지갑</TableCell><TableCell>키 종류</TableCell>
              <TableCell>상태</TableCell><TableCell>KeyStore 인도</TableCell><TableCell>발급일</TableCell>
              <TableCell align="right">조치</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.flatMap((a) =>
              a.wallets.map((w, i) => (
                <TableRow key={w.id} hover>
                  <TableCell>
                    {i === 0 ? (
                      <>
                        <b>{a.loginId}</b>
                        <Typography variant="caption" display="block" color="text.secondary">{a.status}</Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">↳ 이전 지갑</Typography>
                    )}
                  </TableCell>
                  <TableCell><AddressWithCopy address={w.walletAddress} /></TableCell>
                  <TableCell>{w.keyType}</TableCell>
                  <TableCell><KeyStatusChip status={w.keyStatus} /></TableCell>
                  <TableCell>
                    {w.keyType !== 'TYPE1' ? (
                      <Typography variant="caption" color="text.secondary">해당 없음</Typography>
                    ) : a.keystore?.available ? (
                      <Chip size="small" color="warning" label="인도 대기" sx={{ fontWeight: 700 }} />
                    ) : a.keystore?.deliveredAt ? (
                      <Tooltip title={new Date(a.keystore.deliveredAt).toLocaleString('ko-KR')}>
                        <Chip size="small" color="success" label="인도 완료" sx={{ fontWeight: 700 }} />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>{new Date(w.grantedAt).toLocaleDateString('ko-KR')}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => setLogsFor({ acct: a, w })}>이력</Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={w.keyStatus !== 'ACTIVE'}
                        onClick={() => { setErr(null); setPassword(''); setRotate({ acct: a, w }); }}
                      >
                        교체
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={w.keyStatus === 'REVOKED'}
                        onClick={() => { setErr(null); setForce(false); setRevoke({ acct: a, w }); }}
                      >
                        폐기
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              )),
            )}
            {accounts.length === 0 && !list.isLoading && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>지갑이 발급된 계정이 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* 교체 */}
      <Dialog open={!!rotate} onClose={() => setRotate(null)} maxWidth="sm" fullWidth>
        <DialogTitle>키 교체 — {rotate?.acct.loginId}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {err && <Alert severity="error">{err}</Alert>}
            <Alert severity="warning">
              교체하면 <b>새 지갑 주소</b>가 발급되고 온체인 Registry에 다시 등록됩니다.
              기존 지갑의 <b>잔액은 자동으로 이관되지 않습니다</b> — 남은 자산은 전송 또는 강제이전으로 별도 처리해야 합니다.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              기존 지갑 {rotate?.w.walletAddress}
            </Typography>
            {rotate?.w.keyType === 'TYPE1' && (
              <TextField
                label="새 KeyStore 비밀번호"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="Type1은 새 KeyStore가 발급되며 사용자가 마이페이지에서 1회 인도받습니다."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotate(null)}>취소</Button>
          <Button
            variant="contained"
            onClick={() => doRotate.mutate()}
            disabled={doRotate.isPending || (rotate?.w.keyType === 'TYPE1' && !password)}
          >
            {doRotate.isPending ? '교체 중…' : '교체'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 교체 결과 */}
      <Dialog open={!!rotated} onClose={() => setRotated(null)} maxWidth="sm" fullWidth>
        <DialogTitle>키 교체 완료</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2">이전 지갑</Typography>
            <AddressWithCopy address={rotated?.prevWallet} />
            <Typography variant="body2">새 지갑</Typography>
            <AddressWithCopy address={rotated?.newWallet} />
            {rotated?.warning && (
              <Alert severity="warning">
                {rotated.warning}
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {rotated.residual.map((r) => <li key={r.symbol}>{r.formatted} {r.symbol}</li>)}
                </Box>
              </Alert>
            )}
            {rotated?.keystoreDelivery === 'PENDING' && (
              <Alert severity="info">새 KeyStore가 봉인 보관되었습니다. 사용자가 마이페이지에서 1회 내려받습니다.</Alert>
            )}
            <Stack direction="row" spacing={2}>
              <TxHashLink hash={rotated?.txs.addIdentity ?? null} />
              <TxHashLink hash={rotated?.txs.addClaim ?? null} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setRotated(null)}>닫기</Button></DialogActions>
      </Dialog>

      {/* 폐기 */}
      <Dialog open={!!revoke} onClose={() => setRevoke(null)} maxWidth="sm" fullWidth>
        <DialogTitle>키 폐기 — {revoke?.acct.loginId}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {err && <Alert severity="error">{err}</Alert>}
            <Alert severity="error">
              폐기하면 KMS 봉인본이 파기되고 온체인 KYC 클레임도 회수됩니다. <b>되돌릴 수 없습니다.</b>
              잔액이 남아 있으면 해당 자산은 영구히 이동할 수 없게 됩니다.
            </Alert>
            <Typography variant="body2" color="text.secondary">{revoke?.w.walletAddress}</Typography>
            <Button size="small" onClick={() => setForce((v) => !v)} color={force ? 'error' : 'inherit'}>
              {force ? '✓ 잔액이 있어도 강행' : '잔액이 있어도 강행하기'}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoke(null)}>취소</Button>
          <Button color="error" variant="contained" onClick={() => doRevoke.mutate()} disabled={doRevoke.isPending}>
            {doRevoke.isPending ? '폐기 중…' : '폐기'}
          </Button>
        </DialogActions>
      </Dialog>

      <KeyLogsDialog target={logsFor} onClose={() => setLogsFor(null)} />
    </Box>
  );
}

function KeyStatusChip({ status }: { status: KeyWallet['keyStatus'] }) {
  if (status === 'ACTIVE') return <Chip size="small" color="success" label="활성" sx={{ fontWeight: 700 }} />;
  if (status === 'ROTATED') return <Chip size="small" color="info" label="교체됨" sx={{ fontWeight: 700 }} />;
  return <Chip size="small" color="error" label="폐기됨" sx={{ fontWeight: 700 }} />;
}

interface UserKeyHistory {
  id: string;
  after: string;
  changedBy: string;
  onchainTx: string | null;
  changedAt: string;
}

/**
 * 키 생애주기 이력 — **사용자 축**과 **키 축**을 한 화면에 놓는다.
 *
 * RFP 는 "키 폐기 또는 재발급, 교체 시 **사용자와의 이력 연계**가 가능해야 함"을 요구한다.
 * KMS 감사로그(해시체인)는 키 식별자 기준이라 그것만으로는 어떤 사용자의 어떤 지갑이었는지 이어지지 않는다.
 * 그래서 사용자 기준 이력(교체·폐기·유형 변경)을 함께 보여주고, 온체인 tx 로 대조할 수 있게 한다.
 */
function KeyLogsDialog({ target, onClose }: { target: { acct: KeyAccount; w: KeyWallet } | null; onClose: () => void }) {
  const kms = useQuery({
    queryKey: ['key-logs', target?.w.id],
    queryFn: () => api.get<{ keyId: string | null; logs: { event: string; actor?: string; ts?: string; detail?: unknown }[] }>(
      `/admin/keys/${target!.w.id}/logs`,
    ),
    enabled: !!target,
  });
  const userHist = useQuery({
    queryKey: ['user-key-history', target?.acct.userUid],
    queryFn: () => api.get<UserKeyHistory[]>(`/admin/roles/history?userUid=${target!.acct.userUid}`),
    enabled: !!target,
  });

  const keyEvents = (userHist.data ?? []).filter((h) => h.after.startsWith('key:') || h.after.startsWith('keytype:'));

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>키 생애주기 이력 — {target?.acct.loginId}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary">
          {target?.w.walletAddress} · keyId {kms.data?.keyId ?? target?.w.kmsKeyId ?? '—'}
        </Typography>

        <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>사용자 이력 (지갑 연계)</Typography>
        {userHist.isLoading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>시각</TableCell><TableCell>변경</TableCell><TableCell>수행자</TableCell><TableCell>온체인 tx</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {keyEvents.map((h) => (
              <TableRow key={h.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(h.changedAt).toLocaleString('ko-KR')}</TableCell>
                <TableCell>{describeKeyChange(h.after)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{h.changedBy.slice(0, 10)}…</TableCell>
                <TableCell><TxHashLink hash={h.onchainTx} /></TableCell>
              </TableRow>
            ))}
            {!userHist.isLoading && keyEvents.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>교체·폐기 이력이 없습니다(최초 발급 상태).</TableCell></TableRow>
            )}
          </TableBody>
        </Table>

        <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>KMS 감사로그 (해시체인)</Typography>
        {kms.isLoading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>시각</TableCell><TableCell>이벤트</TableCell><TableCell>행위자</TableCell><TableCell>상세</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {(kms.data?.logs ?? []).map((l, i) => (
              <TableRow key={i}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.ts ? new Date(l.ts).toLocaleString('ko-KR') : '—'}</TableCell>
                <TableCell><b>{l.event}</b></TableCell>
                <TableCell>{l.actor ?? '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                  {l.detail ? JSON.stringify(l.detail) : '—'}
                </TableCell>
              </TableRow>
            ))}
            {!kms.isLoading && (kms.data?.logs ?? []).length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>KMS 이력이 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>닫기</Button></DialogActions>
    </Dialog>
  );
}

function describeKeyChange(after: string): string {
  const rotate = /^key:rotate:(0x[0-9a-fA-F]{40})$/.exec(after);
  if (rotate) return `키 교체 → 새 지갑 ${rotate[1].slice(0, 10)}…`;
  const revoke = /^key:revoke:(0x[0-9a-fA-F]{40})$/.exec(after);
  if (revoke) return `키 폐기 — ${revoke[1].slice(0, 10)}…`;
  const kt = /^keytype:requested=(\w+):issued=(\w+)$/.exec(after);
  if (kt) return `키 유형 변경 발급 — 신청 ${kt[1]} → 발급 ${kt[2]}`;
  return after;
}
